import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface ApprovalRequest {
  id: string;
  operationType: string;
  amount: number;
  exceededLimits: string[];
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  requestedAt: string;
  expiresAt: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

export function UserMyApprovals() {
  const { data: approvals, isLoading, error, refetch } = useQuery({
    queryKey: ['user-my-approvals'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.approvals.myApprovals);
      return response.data as ApprovalRequest[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 4 && hoursUntilExpiry > 0;
  };

  const statusConfig: Record<string, { 
    variant: 'success' | 'error' | 'warning' | 'info'; 
    label: string;
    icon: React.ReactNode;
    bgColor: string;
  }> = {
    PENDING: { 
      variant: 'warning', 
      label: 'Aguardando Aprovação',
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      bgColor: 'bg-yellow-500/20',
    },
    APPROVED: { 
      variant: 'success', 
      label: 'Aprovado',
      icon: <CheckCircle className="w-6 h-6 text-green-400" />,
      bgColor: 'bg-green-500/20',
    },
    REJECTED: { 
      variant: 'error', 
      label: 'Rejeitado',
      icon: <XCircle className="w-6 h-6 text-red-400" />,
      bgColor: 'bg-red-500/20',
    },
    EXPIRED: { 
      variant: 'info', 
      label: 'Expirado',
      icon: <AlertTriangle className="w-6 h-6 text-surface-400" />,
      bgColor: 'bg-surface-700',
    },
  };

  const operationLabels: Record<string, string> = {
    PIX_SEND: 'Pix Enviar',
    PIX_RECEIVE: 'Pix Receber',
    TRANSFER: 'Transferência',
    BOLETO: 'Boleto',
    WITHDRAWAL: 'Saque',
  };

  // Group approvals by status
  const pendingApprovals = (approvals || []).filter(a => a.status === 'PENDING');
  const otherApprovals = (approvals || []).filter(a => a.status !== 'PENDING');

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar solicitações</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Minhas Solicitações</h1>
          <p className="text-surface-400">Acompanhe o status das suas solicitações de aprovação</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse bg-surface-800 rounded-xl h-32" />
          ))}
        </div>
      ) : !approvals || approvals.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12 text-surface-400">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma solicitação de aprovação</p>
              <p className="text-sm mt-1">Solicitações são criadas quando você excede seus limites</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          {/* Pending Approvals */}
          {pendingApprovals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Aguardando Aprovação
                  <Badge variant="warning">{pendingApprovals.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-surface-800">
                  {pendingApprovals.map(approval => (
                    <div
                      key={approval.id}
                      className="flex items-center justify-between px-6 py-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isExpiringSoon(approval.expiresAt) ? 'bg-red-500/20' : 'bg-yellow-500/20'
                        }`}>
                          <Clock className={`w-6 h-6 ${
                            isExpiringSoon(approval.expiresAt) ? 'text-red-400' : 'text-yellow-400'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-surface-100">
                            {operationLabels[approval.operationType] || approval.operationType}
                          </p>
                          <p className="text-lg font-bold text-primary-400">
                            {formatCurrency(approval.amount)}
                          </p>
                          <p className="text-xs text-surface-500">
                            Solicitado em {formatDate(approval.requestedAt)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-1 mb-2 justify-end">
                          {approval.exceededLimits.map((limit, i) => (
                            <Badge key={i} variant="warning">{limit}</Badge>
                          ))}
                        </div>
                        {isExpiringSoon(approval.expiresAt) ? (
                          <p className="text-sm text-red-400">Expira em breve</p>
                        ) : (
                          <p className="text-sm text-surface-500">
                            Expira em {formatDate(approval.expiresAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Other Approvals (History) */}
          {otherApprovals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-surface-400" />
                  Histórico
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-surface-800">
                  {otherApprovals.map(approval => {
                    const config = statusConfig[approval.status];
                    return (
                      <div
                        key={approval.id}
                        className="flex items-center justify-between px-6 py-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.bgColor}`}>
                            {config.icon}
                          </div>
                          <div>
                            <p className="font-medium text-surface-100">
                              {operationLabels[approval.operationType] || approval.operationType}
                            </p>
                            <p className="text-lg font-bold text-surface-300">
                              {formatCurrency(approval.amount)}
                            </p>
                            <p className="text-xs text-surface-500">
                              Solicitado em {formatDate(approval.requestedAt)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={config.variant}>{config.label}</Badge>
                          {approval.reviewedAt && (
                            <p className="text-xs text-surface-500 mt-1">
                              Revisado em {formatDate(approval.reviewedAt)}
                            </p>
                          )}
                          {approval.reviewNotes && (
                            <p className="text-xs text-surface-400 mt-1 max-w-xs truncate">
                              "{approval.reviewNotes}"
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Info Card */}
      <Card>
        <CardBody>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="font-medium text-surface-100 mb-1">Como funciona?</h3>
              <p className="text-sm text-surface-400">
                Quando você tenta realizar uma operação que excede seus limites, 
                uma solicitação de aprovação é criada automaticamente. 
                O gerente do seu tenant será notificado e poderá aprovar ou rejeitar a solicitação.
                Solicitações pendentes expiram após 24 horas.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
}
